import { QueryKey, useQuery, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { BidReportQuery } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../../utils";
import { getBidReportInfoForExternalUser } from "../public/getBidReportInfoForExternalUser";
import { useExternalBidQuery } from "../public/useExternalBidQuery";

const bidReportQuery = graphql(`
  query BidReport($request: BidReportRequest!) {
    bidReport(BidReportRequest: $request) {
      id
      createdAt
      updatedAt
      companyId
      teamId
      publicHash
      notice {
        guid
        name
        buyers {
          guid
          name
        }
      }
      bid {
        id
        name
        createdAt
        stage {
          id
        }
      }
    }
  }
`);

export type BidReport = BidReportQuery["bidReport"];

export function useBidReport(
  bidReportId: string,
  options?: UseQueryOptions<BidReportQuery, unknown, BidReportQuery, QueryKey>,
) {
  const { useExternalQuery, urlShareSignature } = useExternalBidQuery();

  const { data, ...rest } = useGraphQlQuery(
    ["bidReport", bidReportId],
    bidReportQuery,
    [{ request: { id: bidReportId, urlShareSignature } }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
      enabled: !useExternalQuery,
    },
  );

  const { data: nonLoggedInData, ...other } = useQuery(
    ["bidReport", bidReportId],
    () => {
      if (urlShareSignature) {
        return getBidReportInfoForExternalUser(bidReportId, urlShareSignature);
      }
    },
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      enabled: useExternalQuery,
    },
  );

  if (!useExternalQuery) {
    return {
      data: data?.bidReport,
      ...rest,
    };
  } else {
    return {
      data: nonLoggedInData,
      ...other,
    };
  }
}
